import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  CircularProgress,
  Link as MuiLink,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import queryString from 'query-string';
import EntremapLogo from '../resources/entremap-icon.png';

const useStyles = makeStyles(() => ({
  shell: { maxWidth: 460, margin: '48px auto 120px', padding: '0 16px', textAlign: 'center' },
  logo: { width: 150, height: 150, margin: '0 auto 16px', display: 'block' },
  title: { fontSize: 36, fontWeight: 600, marginBottom: 6 },
  tagline: { letterSpacing: 2, fontWeight: 600, marginBottom: 18, color: '#333' },
  field: { marginBottom: 14 },
  submitBtn: {
    background: '#7EEAD1',
    color: '#111',
    fontWeight: 700,
    width: '100%',
    height: 44,
    marginTop: 8,
    '&:hover': { background: '#6adcc3' },
  },
  backLink: { marginTop: 12, textAlign: 'center' },
  smallHint: { marginTop: 10, fontSize: 14 },
  errorText: { color: '#c62828', marginBottom: 8 },
  successText: { color: '#1b5e20', marginBottom: 8 },
}));

const ALLOWED_QUT_DOMAINS = ['qut.edu.au', 'connect.qut.edu.au'];
const isEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(s || '').trim());
const isQutEmail = (s) => {
  const email = String(s || '').trim().toLowerCase();
  if (!isEmail(email)) return false;
  const domain = email.split('@').pop();
  return ALLOWED_QUT_DOMAINS.includes(domain);
};
const isStrongPassword = (s) => String(s || '').length >= 8;

export default function QutSignup() {
  const classes = useStyles();
  const { search } = useLocation();
  const params = queryString.parse(search);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [ok, setOk] = useState('');

  useEffect(() => {
    if (typeof params.name === 'string') setName(params.name);
    if (typeof params.email === 'string') setEmail(params.email);
  }, [params.name, params.email]);

  const nameOk = name.trim().length >= 2;
  const emailOk = isQutEmail(email);
  const pwOk = isStrongPassword(pw);
  const matchOk = pw2.length > 0 && pw2 === pw;

  const canSubmit = nameOk && emailOk && pwOk && matchOk && !loading;

  async function onSubmit(e) {
    e.preventDefault();
    setErr('');
    setOk('');
    if (!canSubmit) return;

    try {
      setLoading(true);
      const res = await fetch('/api/auth/qut/signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim().toLowerCase(),
          password: pw,
        }),
      });
      const data = await res.json();

      if (res.status === 409) {
        setErr(data?.message || 'An account already exists for this email.');
        return;
      }
      if (!res.ok || data?.success === false) {
        setErr(data?.message || data?.error || 'Signup failed.');
        return;
      }

      setOk('Account created! Redirecting to login…');
      const next = data?.redirect || '/login';
      setTimeout(() => window.location.assign(next), 700);
    } catch (_e) {
      setErr('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  const emailHelper = (() => {
    if (!email) return ' ';
    if (!isEmail(email)) return 'Please enter a valid email.';
    if (!isQutEmail(email)) return 'Use your QUT email (@qut.edu.au or @connect.qut.edu.au).';
    return ' ';
  })();

  const pwHelper = (() => (!pw || pw.length >= 8 ? 'Minimum 8 characters.' : 'Minimum 8 characters.'))();
  const pw2Helper = (() => (!pw2 ? 'Re-enter your password.' : pw2 === pw ? ' ' : 'Passwords do not match.'))();

  return (
    <div className={`${classes.shell} emap-auth-card`}>
      <img className={classes.logo} src={EntremapLogo} alt="Entremap" />
      <Typography className={classes.title}>Entremap</Typography>
      <Typography className={classes.tagline}>QUT STUDENT SIGN UP</Typography>

      {err && <Typography className={classes.errorText}>{err}</Typography>}
      {ok && <Typography className={classes.successText}>{ok}</Typography>}

      <form onSubmit={onSubmit} noValidate>
        <TextField
          className={classes.field}
          label="Full name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          variant="outlined"
          fullWidth
          required
          error={!!name && !nameOk}
          helperText={!!name && !nameOk ? 'Please enter at least 2 characters.' : ' '}
        />
        <TextField
          className={classes.field}
          label="QUT Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          variant="outlined"
          fullWidth
          required
          error={!!email && !emailOk}
          helperText={emailHelper}
          inputProps={{ pattern: '^[^\\s@]+@(?:qut\\.edu\\.au|connect\\.qut\\.edu\\.au)$' }}
        />
        <TextField
          className={classes.field}
          label="Password"
          type="password"
          value={pw}
          onChange={(e) => setPw(e.target.value)}
          variant="outlined"
          fullWidth
          required
          error={!!pw && !pwOk}
          helperText={pwHelper}
        />
        <TextField
          className={classes.field}
          label="Confirm password"
          type="password"
          value={pw2}
          onChange={(e) => setPw2(e.target.value)}
          variant="outlined"
          fullWidth
          required
          error={!!pw2 && !matchOk}
          helperText={pw2Helper}
        />

        <Button className={classes.submitBtn} type="submit" disabled={!canSubmit}>
          {loading ? <CircularProgress size={22} /> : 'Create Account'}
        </Button>

        <Typography className={classes.smallHint}>
          Already have an account?{' '}
          <MuiLink component={RouterLink} to="/login">
            Log in
          </MuiLink>
          .
        </Typography>

        <div className={classes.backLink}>
          <MuiLink component={RouterLink} to="/login">← Back to login</MuiLink>
        </div>
      </form>
    </div>
  );
}

